import * as React from 'react';

import { enumPaymentStatus } from '@rematter/frontend-lib-api-client';
import {
  Button,
  EntityDetails,
  Loading,
  LocalizedDate,
  Row,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';

import {
  $isPaymentLoading,
  $payment,
  $paymentError,
  $paymentId,
  $tab,
  $voidPayment,
  PaymentDetailsTab,
} from '../../_state/paymentDetail.state';
import { financeRoutes } from '../../routes';
import { FinanceRootLayout } from '../_layouts/FinanceRootLayout';

import { PaymentStatusBadge } from './PaymentStatusBadge';
import { PaymentTabInfo } from './PaymentTabInfo';
import { PaymentTabLinkedDocuments } from './PaymentTabLinkedDocuments';
import { VoidPaymentDialog } from './VoidPaymentDialog';

import { useTranslations } from '@/finance/i18n/useTranslations';
import { useModuleLocation } from '~/shared/hooks/useModuleLocation';

//
// ⚛️ ⚛️ ⚛️ PAYMENT DETAIL VIEW ⚛️ ⚛️ ⚛️
//
export const PaymentDetailView = () => {
  // Translations
  const t = useTranslations();

  // Navigation
  const [, navigate] = useModuleLocation();

  // State
  const [isVoidDialogOpen, setIsVoidDialogOpen] = React.useState(false);

  // Atoms
  const paymentId = useAtomValue($paymentId);
  const payment = useAtomValue($payment);
  const isLoading = useAtomValue($isPaymentLoading);
  const error = useAtomValue($paymentError);
  const [tab, setTab] = useAtom($tab);
  const voidPayment = useSetAtom($voidPayment);

  // Other hooks
  const toast = useToast();

  const activeTab = (tab || 'info') as PaymentDetailsTab;

  // Memoized
  const wasUpdated =
    payment?.timestamps.updatedAt.toString() !==
    payment?.timestamps.createdAt.toString();
  const timestampLabel = wasUpdated
    ? t.payments.detail.updatedOn
    : t.payments.detail.createdOn;
  const timestampDate = wasUpdated
    ? payment?.timestamps.updatedAt
    : payment?.timestamps.createdAt;

  const isDisabled = React.useMemo(
    () => payment?.status === enumPaymentStatus.Voided,
    [payment?.status],
  );

  const infoTabContent = React.useMemo(
    () => () => <PaymentTabInfo disabled={isDisabled} />,
    [isDisabled],
  );

  const linkedDocumentsTabContent = React.useMemo(
    () => () => <PaymentTabLinkedDocuments disabled={isDisabled} />,
    [isDisabled],
  );

  // Callbacks
  const handleBack = React.useCallback(() => {
    navigate(financeRoutes.payments);
  }, [navigate]);

  const handleVoid = React.useCallback(async () => {
    if (!paymentId) return;
    try {
      await voidPayment({ id: paymentId });
      toast.success(t.payments.detail.messages.voidSuccess);
    } catch (err) {
      toast.error(
        t.payments.detail.messages.voidError(
          err instanceof Error ? err.message : 'Unknown error',
        ),
      );
    }
  }, [paymentId, voidPayment, toast, t]);

  if (error) {
    return (
      <FinanceRootLayout>
        <div className="p-4 text-failure">
          {t.payments.detail.messages.loadError(error.message)}
        </div>
      </FinanceRootLayout>
    );
  }

  if (isLoading || !payment) {
    return (
      <FinanceRootLayout>
        <div className="p-4">
          <Loading />
        </div>
      </FinanceRootLayout>
    );
  }

  return (
    <FinanceRootLayout className="@PaymentDetailView">
      <EntityDetails
        onChangeTab={(newTab) => setTab(newTab as PaymentDetailsTab)}
        currentTab={activeTab}
        onBackPressed={handleBack}
        primaryTitle={`Payment #${payment.documentNumber}`}
        subtitle={
          <Row gap1>
            {payment.accountName && (
              <>
                {payment.accountName}
                <span className="mx-1">&middot;</span>
              </>
            )}
            {timestampLabel}{' '}
            <LocalizedDate date={timestampDate} dateStyle="medium" />
          </Row>
        }
        badges={<PaymentStatusBadge status={payment.status} />}
        headerActions={
          !isDisabled && (
            <Button
              variant="outline"
              color="failure"
              onPress={() => setIsVoidDialogOpen(true)}
            >
              {t.payments.detail.actions.void}
            </Button>
          )
        }
        tabs={{
          info: {
            title: t.payments.detail.tabs.info,
            content: infoTabContent,
          },
          'linked-documents': {
            title: t.payments.detail.tabs.linkedDocuments,
            content: linkedDocumentsTabContent,
          },
        }}
      />
      <VoidPaymentDialog
        isOpen={isVoidDialogOpen}
        onClose={() => setIsVoidDialogOpen(false)}
        onConfirm={handleVoid}
        paymentDocumentNumber={payment.documentNumber}
        linkedBillCount={payment.linkedBills?.length ?? 0}
        linkedInvoiceCount={payment.linkedInvoices?.length ?? 0}
        hasFinancialAccount={!!payment.financialAccountId}
      />
    </FinanceRootLayout>
  );
};
